'use client'

import { BriefcaseIcon, Cog6ToothIcon, FilmIcon, HandThumbUpIcon } from '@heroicons/react/24/outline'
import { Button, Divider, Flex, Text, Title } from '@mantine/core'
import { useMutation, useQuery } from '@urql/next'
import { useSelector } from '@xstate/react'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import type React from 'react'
import { Suspense, startTransition, useContext, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'

import { ConnectButton } from '~/app/(accounts)/_components'
import { MarkNotificationAsRead } from '~/app/_graphql'
import { FriendshipState, type FullNotificationFragment } from '~/generated/graphql'

import { AppLayoutContext, IdentityContext, PeerAccountProvider } from '../context'
import { AccountNotificationsContext } from './_components'
import { GetPaginatedNotifications } from './_lib'
import { inViewTriggerDiv, messageWrapper } from './styles.css'
import { SafeAvatarImage, useAppAuth } from '~/app/_components'
import Link from 'next/link'
import { Route } from 'next'
import { notifications } from '@mantine/notifications'

// Icon mapping for different notification types
const NOTIFICATION_ICONS = {
  settingsIcon: <Cog6ToothIcon height={55} color="gray" />,
  filmIcon: <FilmIcon height={55} color="gray" />,
  handThumbUp: <HandThumbUpIcon height={55} color="gray" />,
  briefcaseIcon: <BriefcaseIcon height={55} color="gray" />,
}

// Component for rendering notification icons
function NotificationIcon({ iconUrl }: { iconUrl: string }) {
  if (iconUrl?.startsWith('http')) {
    return <SafeAvatarImage radius="sm" src={iconUrl} />
  }
  return (
    <div>
      {NOTIFICATION_ICONS[iconUrl as keyof typeof NOTIFICATION_ICONS] || <BriefcaseIcon height={55} color="gray" />}
    </div>
  )
}

// Component for action buttons in notifications
function ActionButtons({ notification }: { notification: FullNotificationFragment }) {
  const { mobile } = useContext(AppLayoutContext)
  const { currentAccount } = useContext(IdentityContext)

  const phoneExists =
    currentAccount?.profile?.name === notification.friendship?.requester?.profile?.name
      ? notification.addressePhone
      : notification.requesterPhone

  const handleEmailClick = (hasAccepted?: boolean) => {
    const sendToName =
      currentAccount?.profile?.name === notification.friendship?.requester?.profile?.name
        ? notification.friendship?.addressee?.profile?.name
        : notification.friendship?.requester?.profile?.name

    const sendToEmail =
      currentAccount?.profile?.name === notification.friendship?.requester?.profile?.name
        ? notification.addresseEmail
        : notification.requesterEmail

    const subject = hasAccepted ? `Dome Message: Hi ${sendToName}` : 'Dome Connection Request Reply'
    const msg = `${notification.content}`
    const body = `${sendToName} wrote: \n${msg}\n\n`

    const mailtoLink = hasAccepted
      ? `mailto:${sendToEmail}?subject=${encodeURIComponent(subject)}`
      : msg !== 'null'
        ? `mailto:${sendToEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
        : `mailto:${sendToEmail}?subject=${encodeURIComponent(subject)}`

    window.location.href = mailtoLink
  }

  const handlePhoneClick = () => {
    if (!phoneExists) return
    navigator.clipboard.writeText(phoneExists)
    notifications.show({
      title: 'Copied',
      message: 'Phone number copied to clipboard',
      color: 'green',
      position: 'bottom-left',
    })
  }

  if (!notification.friendship?.requester) return null

  if (
    notification.friendship?.state === FriendshipState.Accepted &&
    notification.title?.includes('Connection Invite from')
  )
    return null

  if (
    notification.friendship?.state === FriendshipState.Accepted &&
    !notification.title?.includes('Connection Invite from')
  ) {
    return (
      <PeerAccountProvider account={notification.friendship.requester}>
        <Flex
          gap={mobile ? '5px' : '5px'}
          align={mobile ? 'flex-start' : 'center'}
          direction={mobile ? 'row' : 'row'}
          w="100%"
        >
          <Button
            onClick={() => handleEmailClick(true)}
            mt={mobile ? '0px' : '3px'}
            style={{
              border: '1px solid black',
              height: '25px',
              backgroundColor: 'black',
              color: 'white',
              fontSize: '12px',
            }}
          >
            EMAIL
          </Button>
          {phoneExists ? (
            <Button
              onClick={handlePhoneClick}
              mt={mobile ? '0px' : '3px'}
              style={{
                border: '1px solid black',
                height: '25px',
                backgroundColor: 'black',
                color: 'white',
                fontSize: '12px',
              }}
            >
              PHONE
            </Button>
          ) : null}
        </Flex>
      </PeerAccountProvider>
    )
  }

  return (
    <PeerAccountProvider account={notification.friendship.requester}>
      <Flex
        gap={mobile ? '5px' : '6px'}
        align={mobile ? 'flex-start' : 'center'}
        direction={mobile ? 'row' : 'row'}
        w="100%"
        maw="100%"
        wrap="wrap"
      >
        <div style={{ maxWidth: mobile ? undefined : '60%' }}>
          <ConnectButton preset="notifications" />
        </div>
      </Flex>
    </PeerAccountProvider>
  )
}

// Component for rendering a single notification
function NotificationItem({ notification }: { notification: FullNotificationFragment }) {
  const [, update] = useMutation(MarkNotificationAsRead)

  const handleNotificationClick = async () => {
    await update({})
  }

  return (
    <div key={notification.id}>
      <Flex
        p="sm"
        className={messageWrapper}
        align="flex-start"
        gap="md"
        onClick={handleNotificationClick}
        data-testid="notification-title"
        style={{
          backgroundColor: notification.isRead ? undefined : '#f1f3f5',
        }}
      >
        <Flex direction="column" justify="center" h="100%">
          <NotificationIcon iconUrl={notification.icon ?? ''} />
        </Flex>

        <Flex direction="column" justify="center" h="100%" w="100%">
          <Text data-testid="notification-avatar" size="lg">
            {notification.titleUrl ? (
              <Link href={notification.titleUrl as Route} style={{ textDecoration: 'none', color: 'black' }}>
                {notification.title ?? ''}
              </Link>
            ) : (
              notification.title
            )}
          </Text>
          <Text
            size="sm"
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: '1.5',
              marginTop: '4px',
              color: '#555',
            }}
          >
            {notification.content ?? ''}
          </Text>

          {notification.friendship?.requester &&
          notification.friendship?.state === FriendshipState.Accepted &&
          notification.title?.includes('Connection Invite from')
            ? null
            : null}

          {notification.friendship?.requester &&
          notification.friendship?.state === FriendshipState.Accepted &&
          !notification.title?.includes('Connection Invite from') ? (
            <ActionButtons notification={notification} />
          ) : null}

          {notification.friendship?.requester &&
          notification.friendship?.state !== FriendshipState.Rejected &&
          notification.friendship?.state !== FriendshipState.Accepted ? (
            <ActionButtons notification={notification} />
          ) : null}

          <Title order={6} mt="5px">
            {dayjs(notification.createdAt).fromNow()}
          </Title>
        </Flex>
      </Flex>
      <Divider color={notification.isRead ? '#f1f1f1' : 'white'} />
    </div>
  )
}

// Component for rendering the list of notifications
export function NotificationsList({
  edges,
}: {
  edges: { node: FullNotificationFragment; cursor: string }[]
}) {
  dayjs.extend(relativeTime)

  return (
    <div data-testid="accept-wrapper">
      {edges.map((edge) => (
        <NotificationItem key={edge.cursor} notification={edge.node} />
      ))}
    </div>
  )
}

// Component for fetching and rendering paginated notifications
function NotificationsResultInner({ page, skipRendering }: { page: number; read?: boolean; skipRendering?: boolean }) {
  const { actor, query } = useContext(AccountNotificationsContext)
  const variables = useSelector(actor, (s) => s.context.pageArgsList[page])
  const [{ data, fetching, error }] = useQuery({
    query: GetPaginatedNotifications,
    variables: {
      ...variables,
      readState: query,
    },
    pause: !variables,
  })

  useEffect(() => {
    if (data?.currentAccount?.__typename === 'Error') {
      // Handle error case
    } else {
      const pageInfo = data?.currentAccount?.notifications?.pageInfo
      const totalCount = data?.currentAccount?.notifications?.totalCount

      startTransition(() => {
        if (pageInfo) {
          actor.send({
            type: 'UPDATE_PAGE_INFO',
            pageNumber: page,
            pageInfo,
            totalCount,
          })
        }
      })
    }
  }, [data])

  if (skipRendering) return null

  if (data?.currentAccount?.__typename === 'Error') {
    return null
  }

  const notificationsEdges = data?.currentAccount?.notifications?.edges || []
  return <NotificationsList edges={notificationsEdges} />
}

// Component for handling infinite scroll
function ScrollFetcher({ children }: React.PropsWithChildren) {
  const { actor } = useContext(AccountNotificationsContext)
  const actorState = useSelector(actor, (s) => s)

  function notifyMachine(inView: boolean) {
    if (inView) startTransition(() => actor.send({ type: 'NEXT_PAGE' }))
  }

  const { ref } = useInView({
    rootMargin: '1000px 0px',
    triggerOnce: true,
    initialInView: false,
    onChange: notifyMachine,
  })

  const inViewKey = `notifications-page-${actorState.context.lastLoadedPage}`

  return (
    <>
      {children}
      <div className={inViewTriggerDiv} key={inViewKey} ref={ref} />
    </>
  )
}

// Main component for the notifications list
export function NotificationsListComponent({ skipRendering = false }: { skipRendering?: boolean } = {}) {
  const { actor } = useContext(AccountNotificationsContext)
  const pages = useSelector(actor, (state) => Object.keys(state.context.pageArgsList).map(Number))
  const { isSignedIn } = useAppAuth()
  if (!isSignedIn) return null
  // WARNING
  // the suspense around SearchResultPage is required
  // otherwise the higher level suspense will be triggered
  // and the scroll will jump
  return (
    <ScrollFetcher>
      <>
        {pages.map((page) => (
          <Suspense key={page}>
            <NotificationsResultInner page={page} key={page} skipRendering={skipRendering} />
          </Suspense>
        ))}
      </>
    </ScrollFetcher>
  )
}
